<template>
	<div class="form-designer-content__input-fields">
		<div class="form-designer-content__input-btn">
			<Button
				className="btn--has-icon btn--small"
				labelText="Add Input Field"
				:iconBeforeLabel="true"
				iconClassName="icon-more"
				:disabled="hasFieldsError"
				:btnClick="addInputField"
				v-if="editMode"
			/>
		</div>
		<div class="form-designer-content drag-drop">
			<div class="drag-drop__header" v-show="inputFieldsData.length > 0">
				<span class="drag-drop__header-title"
					>Drag and Drop to update the order or change the field
					<i class="drag-drop__header-emphasized"
						>&nbsp;Prompt Order</i
					>
				</span>
			</div>
			<draggable
				class="drag-drop__body"
				v-model="inputFieldsData"
				v-bind="dragOptions"
				@end="dropHandler"
				@start="drag = true"
			>
				<transition-group
					class="drag-drop__list"
					type="transition"
					tag="ol"
					:name="!drag ? 'transition-transform-movement' : null"
				>
					<FieldPromptItem
						v-for="inputField in inputFieldsData"
						:key="inputField[mapping.id]"
						:value="inputField"
						:inputFieldsNames="getDropdownItems"
						:editMode="editMode"
						:mapping="mapping"
						@changePromptOrder="changePromptOrderHandler"
						@input="changeFieldNameHandler"
						@delete="deleteInputFieldHandler"
						@hasErrors="handlerFieldsError"
					/>
				</transition-group>
			</draggable>
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import FieldPromptItem from '@/views/form-designer/field-prompt-item.vue';
import draggable from 'vuedraggable';
import { getNextNumber } from '@/utils/function';
import { mapActions } from 'vuex';
import { SET_HAS_ERROR } from '@/store/modules/mutation-types.js';
import _ from 'lodash';

export default {
	name: 'FieldPromptDragableList',
	components: {
		Button,
		FieldPromptItem,
		draggable
	},
	model: {
		prop: 'inputFields',
		event: 'change'
	},
	props: {
		inputFields: {
			type: Array,
			default: () => []
		},
		editMode: {
			type: Boolean,
			required: true
		},
		startOrder: {
			type: Number,
			default: 1
		},
		mapping: {
			type: Object,
			default: () => {
				return {
					name: 'keyboardPrompt',
					order: 'order',
					id: 'id'
				};
			}
		}
	},
	data() {
		return {
			inputFieldsWithError: [],
			errorCount: 0,
			inputFieldsData: [],
			drag: false
		};
	},
	computed: {
		hasInputFields() {
			return this.inputFieldsData.length > 0;
		},
		hasFieldsError() {
			let withErrors = this.inputFieldsWithError.length > 0;
			let longitud = this.inputFieldsData.length > 0;
			let emptyname = this.inputFieldsData.some(
				field => !field[this.mapping.name]
			);
			let condition = withErrors || (longitud && emptyname);
			return condition;
		},
		getDropdownItems() {
			return this.inputFieldsData.map(item => {
				return {
					text: item[this.mapping.order].toString(),
					value: item[this.mapping.order]
				};
			});
		},
		dragOptions() {
			return {
				animation: 200,
				group: 'list-elements',
				disabled: false,
				ghostClass: 'is-ghost',
				chosenClass: 'is-sortable-chosen'
			};
		}
	},
	methods: {
		...mapActions('form', {
			setErrorValue: SET_HAS_ERROR
		}),

		dropHandler() {
			this.drag = false;
		},
		changePromptOrderHandler(e) {
			let list = this.inputFieldsData.map(x => {
				return Object.assign({}, x);
			});
			const pos = this.findInputFieldBySqlId(e.formsKeyboardFieldsId);
			if (pos >= 0) {
				list[pos][this.mapping.order] = e[this.mapping.order];
				list.splice(pos, 1);
			}
			list.splice(e[this.mapping.order] - this.startOrder, 0, e);
			this.inputFieldsData = list;
		},
		changeFieldNameHandler(e) {
			let item = this.inputFieldsData.find(
				elem => elem[this.mapping.id] === e[this.mapping.id]
			);
			if (item) {
				item[this.mapping.name] = e[this.mapping.name];
			}
		},
		deleteInputFieldHandler(inputFieldId) {
			const pos = this.findInputFieldBySqlId(inputFieldId);
			if (pos > -1) {
				this.inputFieldsData.splice(pos, 1);
			}
		},
		findInputFieldBySqlId(id) {
			return this.inputFieldsData.findIndex(
				x => x[this.mapping.id] === id
			);
		},
		addInputField() {
			let fields = this.inputFieldsData;

			const nextOrder = getNextNumber(
				fields.map(item => item[this.mapping.order])
			);
			const nextId = getNextNumber(
				fields.map(item => item[this.mapping.id]),
				true
			);

			fields.push({
				[this.mapping.name]: '',
				[this.mapping.order]: nextOrder,
				[this.mapping.id]: nextId
			});
		},
		handlerFieldsError(obj) {
			const idx = this.inputFieldsWithError.findIndex(
				x => x[this.mapping.id] === obj.id
			);
			if (obj.hasError) {
				if (idx < 0) {
					this.inputFieldsWithError.push({ ...obj });
				}
			} else {
				if (idx > -1) {
					this.inputFieldsWithError.splice(idx, 1);
					this.inputFieldsData.findIndex((elem, index) => {
						if (elem[this.mapping.id] === obj[this.mapping.id]) {
							this.inputFieldsData[index].errorMsg = '';
							return index;
						}
					});
				}
			}
		}
	},
	watch: {
		inputFields: {
			deep: true,
			immediate: true,
			handler(list) {
				this.inputFieldsData = list.map(item =>
					Object.assign({}, item)
				);
			}
		},
		inputFieldsData: {
			deep: true,
			handler(aList) {
				if (!_.isEqual(aList, this.inputFields)) {
					let list = aList.map((item, index) =>
						Object.assign({}, item, {
							[this.mapping.order]: index + this.startOrder
						})
					);

					list.forEach(element => {
						if (
							element.errorMsg &&
							element.errorMsg === 'duplicated name'
						) {
							element.errorMsg = '';
							element.hasError = false;
						}
					});

					this.inputFieldsWithError = this.inputFieldsWithError.filter(
						i => {
							return i.errorMsg !== 'duplicated name';
						}
					);

					for (let i = 0; i <= list.length - 2; i++) {
						for (let j = i + 1; j <= list.length - 1; j++) {
							if (
								list[i][this.mapping.name].toLowerCase() ===
								list[j][this.mapping.name].toLowerCase()
							) {
								list[i].errorMsg = 'duplicated name';
								list[i].hasError = true;
								this.handlerFieldsError(list[i]);
								list[j].errorMsg = 'duplicated name';
								list[j].hasError = true;
								this.handlerFieldsError(list[j]);
							}
						}
					}

					this.$emit('change', list);
				}
			}
		},
		hasFieldsError: {
			handler(val) {
				this.setErrorValue(val);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * Laser Form content - Drag and Drop 
*/

.form-designer-content {
	&__input-fields {
		padding: $padding-md;
	}

	.drag-drop {
		&__body {
			max-height: 360px;
		}
	}
}

// Drag Drop styles - DO NOT REUSE; use draggable-list component control if you need drag drop behavior
.drag-drop {
	&__header {
		display: flex;
		flex-wrap: wrap;
		margin-top: $gutter-lg;

		&-title {
			font-size: $default-font-size - 2;
			margin: $gutter-md 0 $gutter-sm;
		}
	}

	&__body {
		width: 100%;
		margin-top: $gutter-md;
		overflow-y: auto;
		@include scrollbar;

		@include mq($from: md) {
			width: fit-content;
		}
	}

	&__list {
		width: 100%;
		padding: $padding-xs 0;
		@include transition-transform-movement;
		display: flex;
		flex-direction: column;
		gap: $gutter-xl;
	}
}
</style>
